import Trix from "trix";

// Register h2–h6 as block types so Trix preserves API-generated headings when
// the user edits content that contains them. Users cannot create these from the
// toolbar, but existing headings survive editing sessions without being degraded
// to plain paragraphs.
(["h2", "h3", "h4", "h5", "h6"] as const).forEach((tagName, zeroBasedIndex) => {
  Trix.config.blockAttributes[`heading${zeroBasedIndex + 2}`] = {
    tagName,
    terminal: true,
    breakOnReturn: true,
    group: false,
  };
});

// Allow relative URLs in Trix link dialog
document.addEventListener("trix-initialize", (event: any) => {
  const {toolbarElement} = event.target;
  const inputElement = toolbarElement.querySelector("input[name=href]");

  if (inputElement) {
    inputElement.type = "text";
    inputElement.pattern = "(https?://|/).+";
  }

  const applyDisable = (target) => {
    const disabled = target.hasAttribute("disabled");
    target.toggleAttribute("contenteditable", !disabled);
  };

  const observer = new MutationObserver((mutations) => {
    for (const {target, type} of mutations) {
      if (type == "attributes") {
        applyDisable(target);
      }
    }
  });

  observer.observe(event.target, {attributeFilter: ["disabled"]});
  applyDisable(event.target);

  // Trix's linear document model cannot represent table structure, so raw
  // <table> elements are stripped on edit. Convert any tables found in the
  // original server HTML to Trix content attachments so they survive as
  // opaque, non-editable blocks. event.target.defaultValue holds the server
  // HTML before Trix processes it.
  const originalHTML: string = event.target.defaultValue;
  if (originalHTML) {
    const container = document.createElement("div");
    container.innerHTML = originalHTML;
    const tables = Array.from(container.querySelectorAll("table"));
    if (tables.length > 0) {
      tables.forEach((table) => {
        const figure = document.createElement("figure");
        figure.setAttribute(
          "data-trix-attachment",
          JSON.stringify({contentType: "text/html", content: table.outerHTML})
        );
        table.replaceWith(figure);
      });
      event.target.editor.loadHTML(container.innerHTML);
    }
  }
});

// Handle paste of content into Trix editors
document.addEventListener("trix-before-paste", (event: any) => {
  if (event?.paste?.html) {
    let html = removeLocalFileImages(event.paste.html);

    if (isMinimalEditor(event.target)) {
      html = removeBlockFormattingElements(html);
    }

    event.paste.html = html;
  }
});

// Prevent file attachments in minimal Trix editors
document.addEventListener("trix-file-accept", function(event) {
  if (isMinimalEditor(event.target)) {
    event.preventDefault();
  }
});

const removeLocalFileImages = (html: string) =>
  html.replace(/<img\b[^>]*\bsrc=['"]?(file:|blob:)[^'">]+['"]?[^>]*>/gi, "");

const isMinimalEditor = (editorElement: any): boolean =>
  editorElement instanceof Element && !!editorElement.closest(".trix-minimal");

const removeBlockFormattingElements = (html: string): string => {
  const container = document.createElement("div");
  container.innerHTML = html;

  const blockElements = container.querySelectorAll("blockquote, h1, h2, h3, h4, h5, h6, pre, ul, ol, li");

  Array.from(blockElements).forEach((element) => {
    element.replaceWith(...Array.from(element.childNodes));
  });

  return container.innerHTML;
};
